import React, { useState, useEffect, useRef } from "react";

const Logo: React.FC = () => {
  const fullText = "KRAFFCHO.DEV";
  const [displayText, setDisplayText] = useState("");
  const [showCursor, setShowCursor] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const logoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let currentIndex = 0;
    let hasCompleted = false;
    let blinkIntervalRef: NodeJS.Timeout | null = null;
    let cursorShown = false;

    intervalRef.current = setInterval(() => {
      if (currentIndex < fullText.length) {
        setDisplayText(fullText.slice(0, currentIndex + 1));
        currentIndex++;

        // Show cursor after several letters have appeared (around 4-5 letters)
        if (currentIndex >= 5 && !cursorShown) {
          cursorShown = true;
          setShowCursor(true);
        }
      } else if (!hasCompleted) {
        // Typing complete - blink cursor 2 more times then hide
        hasCompleted = true;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        // Small delay before starting blink sequence
        timeoutRef.current = setTimeout(() => {
          // Blink 2 more times (on-off-on-off = 4 state changes)
          let blinkCount = 0;
          blinkIntervalRef = setInterval(() => {
            blinkCount++;
            if (blinkCount <= 4) {
              setShowCursor(blinkCount % 2 === 1);
            } else {
              if (blinkIntervalRef) {
                clearInterval(blinkIntervalRef);
              }
              setShowCursor(false);
            }
          }, 300);
        }, 200);
      }
    }, 100); // Faster typing speed

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (blinkIntervalRef) {
        clearInterval(blinkIntervalRef);
      }
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLSpanElement>) => {
    const textElement = e.currentTarget;
    const rect = textElement.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  };

  const letters = displayText.split("");

  return (
    <div className="site-logo" ref={logoRef}>
      <span
        className="site-logo__text"
        aria-label="kraffcho.dev"
        onMouseMove={handleMouseMove}
      >
        {letters.map((letter, index) => {
          // Calculate position-based glow intensity for each letter
          const letterPosition = (index / Math.max(1, letters.length - 1)) * 100;
          const distanceFromMouse = Math.abs(mousePosition.x - letterPosition);
          const glowIntensity = Math.max(0.3, Math.min(1, 1 - distanceFromMouse / 40));

          // Calculate glow sizes based on intensity
          const baseGlow = 15 + glowIntensity * 25;
          const midGlow = 25 + glowIntensity * 35;
          const farGlow = 40 + glowIntensity * 50;

          // Calculate opacity based on intensity
          const baseOpacity = 0.6 + glowIntensity * 0.4;
          const midOpacity = 0.4 + glowIntensity * 0.3;
          const farOpacity = 0.2 + glowIntensity * 0.3;

          return (
            <span
              key={index}
              className={`site-logo__letter ${letter === "." ? "site-logo__dot" : ""}`}
              style={{
                animationDelay: `${index * 0.05}s`,
                textShadow: letter === "."
                  ? `0 0 25px rgba(78, 205, 196, 1), 0 0 40px rgba(78, 205, 196, 0.8), 0 0 60px rgba(253, 177, 236, ${0.3 + glowIntensity * 0.4})`
                  : `0 0 ${baseGlow}px rgba(253, 177, 236, ${baseOpacity}), 0 0 ${midGlow}px rgba(253, 177, 236, ${midOpacity}), 0 0 ${farGlow}px rgba(78, 205, 196, ${farOpacity})`,
              } as React.CSSProperties}
            >
              {letter}
            </span>
          );
        })}
        {showCursor && <span className="site-logo__cursor typing"></span>}
      </span>
    </div>
  );
};

export default Logo;

